import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const Withdrawal = () => {
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [showHistory, setShowHistory] = useState(true);

  const UserId = localStorage.getItem("UserId");

  // Fetch wallet balance
  const fetchBalance = useCallback(async () => {
    try {
      setBalanceLoading(true);
      const res = await axios.post(`${API_BASE}/walletbalance`, { UserId });
      if (res.data.mainbal !== undefined) setBalance(res.data.mainbal);
      else toast.warning("Unable to fetch wallet balance");
    } catch (err) {
      console.error("Balance Error:", err);
      toast.error("Failed to load wallet balance");
    } finally {
      setBalanceLoading(false);
    }
  }, [UserId]);

  // Fetch withdrawal status
  const fetchWithdrawalStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/withdrawal-status/${UserId}`);
      if (res.data.status === 1) {
        setWithdrawals(res.data.withdrawals || []);
        setPendingCount(res.data.pending_count || 0);
      }
    } catch (err) {
      console.error("Error fetching withdrawal status:", err);
    }
  }, [UserId]);

  // Initial load
  useEffect(() => {
    fetchBalance();
    fetchWithdrawalStatus();
  }, [fetchBalance, fetchWithdrawalStatus]);

  // Auto-refresh status every 30 seconds if there are pending requests
  useEffect(() => {
    if (pendingCount === 0) return;

    const interval = setInterval(() => {
      fetchWithdrawalStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [pendingCount, fetchWithdrawalStatus]);

  // Handle Withdrawal Request
  const handleWithdraw = async (e) => {
    e.preventDefault();

    if (!amount) {
      toast.warning("Please enter withdrawal amount");
      return;
    }

    const amountNum = parseFloat(amount);

    if (amountNum < 10) {
      toast.error("Minimum withdrawal is $10");
      return;
    }

    if (amountNum > balance) {
      toast.error("Insufficient balance");
      return;
    }

    try {
      setLoading(true);
      toast.info("Submitting withdrawal request...");

      const res = await axios.post(`${API_BASE}/request-withdrawal`, {
        userId: UserId,
        amount: amountNum,
        tokenAmount: amountNum,
        currency: 'USDT.BEP20'
      });

      const data = res.data;

      if (data.status === 1) {
        toast.success("✅ Withdrawal request submitted successfully! Awaiting admin approval.");
        setAmount("");
        await fetchBalance();
        await fetchWithdrawalStatus();
      } else if (data.status === 2) {
        toast.warning(data.message || "Please update your wallet address first");
      } else {
        toast.error(data.message || "Withdrawal request failed");
      }
    } catch (err) {
      console.error("Withdrawal Error:", err);
      toast.error("Transaction failed or server error!");
    } finally {
      setLoading(false);
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch(status) {
      case 0:
        return <span className="badge badge-pending">⏳ Pending</span>;
      case 1:
        return <span className="badge badge-completed">✅ Completed</span>;
      case -1:
        return <span className="badge badge-failed">❌ Rejected/Failed</span>;
      default:
        return <span className="badge badge-unknown">Unknown</span>;
    }
  };

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="withdrawal-page">
        <div className="page-header">
          <h1>Withdrawal Funds</h1>
          <p>Withdraw your earnings to your wallet via CoinPayments</p>
          {pendingCount > 0 && (
            <div className="pending-notice">
              <i className="fa fa-clock-o"></i> 
              You have {pendingCount} pending withdrawal request{pendingCount > 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div className="withdrawal-container">
          {/* Balance Card */}
          <div className="balance-card">
            <h3>Available Balance</h3>
            <div className="balance-amount">
              {balanceLoading ? (
                <div className="loading-text">Loading...</div>
              ) : (
                <>${balance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</>
              )}
            </div>
            <p>Minimum withdrawal: $10</p>
          </div>

          {/* Withdrawal Form */}
          <div className="withdraw-card">
            <h2>Request Withdrawal</h2>
            <form onSubmit={handleWithdraw}>
              <div className="form-group">
                <label>Withdrawal Amount ($)</label>
                <div className="input-wrapper">
                  <i className="fa fa-dollar"></i>
                  <input
                    type="number"
                    placeholder="Enter amount in USD"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={loading || pendingCount > 0}
                    step="0.01"
                    min="10"
                    required
                  />
                </div>
              </div>

              {pendingCount > 0 && (
                <div className="warning-box">
                  <i className="fa fa-info-circle"></i>
                  You already have a pending withdrawal request. Please wait for admin approval before submitting another.
                </div>
              )}

              <div className="withdrawal-info">
                <h4>Withdrawal Information:</h4>
                <ul>
                  <li><i className="fa fa-check-circle"></i> Processing time: 24-48 hours</li>
                  <li><i className="fa fa-arrow-up"></i> Minimum withdrawal: $10</li>
                  <li><i className="fa fa-arrow-right"></i> Network: BEP20 (USDT)</li>
                </ul>
              </div>

              <button 
                type="submit" 
                className="btn-primary" 
                disabled={loading || pendingCount > 0}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i> SUBMITTING...
                  </>
                ) : pendingCount > 0 ? (
                  <>
                    <i className="fa fa-clock-o"></i> PENDING REQUEST
                  </>
                ) : (
                  <>
                    <i className="fa fa-money"></i> REQUEST WITHDRAWAL
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Withdrawal History */}
          <div className="history-section">
            <div className="history-header" onClick={() => setShowHistory(!showHistory)}>
              <h3>
                <i className="fa fa-history"></i> 
                Withdrawal History 
                <span className="history-count">({withdrawals.length})</span>
              </h3>
              <i className={`fa fa-chevron-${showHistory ? 'up' : 'down'}`}></i>
            </div>
            
            {showHistory && (
              <div className="history-body">
                {withdrawals.length === 0 ? (
                  <p className="no-history">No withdrawal requests found</p>
                ) : (
                  <div className="table-responsive">
                    <table className="history-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Amount</th>
                          <th>Token</th>
                          <th>Status</th>
                          <th>Date</th>
                          <th>TX Hash</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.map((w) => (
                          <tr key={w.id}>
                            <td data-label="ID">{w.id}</td>
                            <td data-label="Amount">${w.amount}</td>
                            <td data-label="Token">{w.tokenamount} USDT</td>
                            <td data-label="Status">{getStatusBadge(w.txstatus)}</td>
                            <td data-label="Date">{new Date(w.created_at).toLocaleDateString()}</td>
                            <td data-label="TX Hash">
                              {w.txhash ? (
                                <span className="tx-hash" title={w.txhash}>
                                  {w.txhash.length > 12 ? w.txhash.substring(0, 12) + '...' : w.txhash}
                                </span>
                              ) : (
                                <span className="tx-pending">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .withdrawal-page {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 30px;
        }

        .page-header h1 {
          font-size: 28px;
          margin-bottom: 5px;
          color: #333;
        }

        .page-header p {
          color: #666;
          font-size: 16px;
        }

        .pending-notice {
          background: #FFF3CD;
          border: 1px solid #FFC107;
          padding: 10px 15px;
          border-radius: 5px;
          margin-top: 10px;
          color: #856404;
          font-size: 14px;
        }

        .withdrawal-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 30px;
        }

        .balance-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        .balance-card h3 {
          font-size: 18px;
          opacity: 0.9;
          margin-bottom: 10px;
        }

        .balance-amount {
          font-size: 42px;
          font-weight: bold;
          margin: 15px 0;
        }

        .balance-card p {
          opacity: 0.8;
          font-size: 14px;
        }

        .withdraw-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.08);
        }

        .withdraw-card h2 {
          font-size: 22px;
          margin-bottom: 20px;
          color: #333;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          color: #555;
        }

        .input-wrapper {
          position: relative;
        }

        .input-wrapper i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }

        .input-wrapper input {
          width: 100%;
          padding: 12px 15px 12px 35px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 16px;
          transition: border-color 0.3s;
        }

        .input-wrapper input:focus {
          outline: none;
          border-color: #667eea;
        }

        .input-wrapper input:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .warning-box {
          background: #FFF3CD;
          border: 1px solid #FFC107;
          padding: 12px 15px;
          border-radius: 8px;
          margin-bottom: 20px;
          color: #856404;
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 14px;
        }

        .warning-box i {
          font-size: 18px;
          flex-shrink: 0;
        }

        .withdrawal-info {
          background: #f8f9fa;
          padding: 15px 20px;
          border-radius: 8px;
          margin: 20px 0;
        }

        .withdrawal-info h4 {
          margin-bottom: 10px;
          color: #333;
        }

        .withdrawal-info ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .withdrawal-info ul li {
          padding: 5px 0;
          color: #555;
          font-size: 14px;
        }

        .withdrawal-info ul li i {
          margin-right: 10px;
          color: #667eea;
          width: 18px;
        }

        .btn-primary {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.3s, opacity 0.3s;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          opacity: 0.9;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .history-section {
          grid-column: 1 / -1;
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 20px rgba(0,0,0,0.08);
          margin-top: 20px;
          overflow: hidden;
        }

        .history-header {
          padding: 20px 25px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.3s;
        }

        .history-header:hover {
          background: #f8f9fa;
        }

        .history-header h3 {
          margin: 0;
          color: #333;
          font-size: 18px;
        }

        .history-count {
          font-weight: normal;
          color: #888;
          font-size: 14px;
        }

        .history-header i {
          color: #888;
        }

        .history-body {
          padding: 20px 25px;
        }

        .no-history {
          text-align: center;
          color: #888;
          padding: 30px 0;
          font-size: 16px;
        }

        .table-responsive {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .history-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 600px;
        }

        .history-table th {
          text-align: left;
          padding: 12px 15px;
          background: #f8f9fa;
          font-weight: 600;
          color: #555;
          border-bottom: 2px solid #e0e0e0;
          font-size: 13px;
        }

        .history-table td {
          padding: 12px 15px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 14px;
        }

        .history-table tr:hover {
          background: #f8f9fa;
        }

        .badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }

        .badge-pending {
          background: #FFF3CD;
          color: #856404;
        }

        .badge-completed {
          background: #D4EDDA;
          color: #155724;
        }

        .badge-failed {
          background: #F8D7DA;
          color: #721C24;
        }

        .badge-unknown {
          background: #E2E3E5;
          color: #383D41;
        }

        .tx-hash {
          font-size: 12px;
          font-family: 'Courier New', monospace;
          color: #555;
          cursor: help;
        }

        .tx-pending {
          color: #999;
          font-size: 13px;
        }

        /* ✅ Mobile Responsive Styles */
        @media (max-width: 768px) {
          .withdrawal-page {
            padding: 10px;
          }

          .page-header h1 {
            font-size: 22px;
          }

          .page-header p {
            font-size: 14px;
          }

          .pending-notice {
            font-size: 13px;
            padding: 8px 12px;
          }

          .withdrawal-container {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .balance-card {
            padding: 20px;
          }

          .balance-amount {
            font-size: 32px;
          }

          .withdraw-card {
            padding: 20px;
          }

          .withdraw-card h2 {
            font-size: 20px;
          }

          .form-group label {
            font-size: 14px;
          }

          .input-wrapper input {
            font-size: 14px;
            padding: 10px 12px 10px 35px;
          }

          .withdrawal-info ul li {
            font-size: 13px;
          }

          .btn-primary {
            font-size: 14px;
            padding: 12px;
          }

          .history-header {
            padding: 15px 20px;
          }

          .history-header h3 {
            font-size: 16px;
          }

          .history-body {
            padding: 15px;
            overflow-x: auto;
          }

          .history-table {
            min-width: 500px;
            font-size: 13px;
          }

          .history-table th,
          .history-table td {
            padding: 10px 12px;
            font-size: 12px;
          }

          .badge {
            font-size: 11px;
            padding: 3px 10px;
          }

          .tx-hash {
            font-size: 11px;
          }
        }

        /* ✅ Extra Small Devices (Phones) */
        @media (max-width: 480px) {
          .withdrawal-page {
            padding: 8px;
          }

          .page-header h1 {
            font-size: 20px;
          }

          .balance-amount {
            font-size: 28px;
          }

          .balance-card {
            padding: 15px;
          }

          .withdraw-card {
            padding: 15px;
          }

          .withdraw-card h2 {
            font-size: 18px;
          }

          .input-wrapper input {
            font-size: 13px;
            padding: 8px 10px 8px 32px;
          }

          .history-table {
            min-width: 400px;
            font-size: 12px;
          }

          .history-table th,
          .history-table td {
            padding: 8px 10px;
            font-size: 11px;
          }

          .history-header {
            padding: 12px 15px;
          }

          .history-header h3 {
            font-size: 14px;
          }

          .history-body {
            padding: 10px;
          }

          .no-history {
            font-size: 14px;
            padding: 20px 0;
          }

          .warning-box {
            font-size: 13px;
            padding: 10px 12px;
          }

          .warning-box i {
            font-size: 16px;
          }

          .withdrawal-info h4 {
            font-size: 14px;
          }

          .withdrawal-info ul li {
            font-size: 12px;
            padding: 4px 0;
          }

          .btn-primary {
            font-size: 13px;
            padding: 10px;
          }

          .badge {
            font-size: 10px;
            padding: 2px 8px;
          }

          .tx-hash {
            font-size: 10px;
          }
        }

        /* ✅ Landscape Phones */
        @media (max-width: 768px) and (orientation: landscape) {
          .withdrawal-container {
            grid-template-columns: 1fr 1fr;
          }

          .balance-card {
            padding: 15px;
          }

          .balance-amount {
            font-size: 28px;
          }
        }

        /* ✅ Tablets */
        @media (min-width: 769px) and (max-width: 1024px) {
          .withdrawal-container {
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }

          .balance-amount {
            font-size: 36px;
          }
        }

        /* ✅ Large Screens */
        @media (min-width: 1400px) {
          .withdrawal-page {
            max-width: 1400px;
          }

          .balance-amount {
            font-size: 48px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default Withdrawal;